import { useEffect, useState } from "react";
import { getSeoConfig } from "@/api/homeApi";

export const SITE_ORIGIN = "https://www.ogolden.com";
export const DEFAULT_IMAGE = `${SITE_ORIGIN}/favicon.png`;

// 默认值（fallback，API 未返回时使用）
const FALLBACK_SITE_NAME = "欧格登";
const FALLBACK_KEYWORDS = [
  "欧格登",
  "欧格登智能",
  "欧格登科技",
  "ogolden",
  "欧格登官网",
  "欧格登智能科技",
  "企业安全解决方案",
  "信息安全",
  "网络安全",
  "IT基础架构",
  "弱电智能化",
  "等保三级",
  "零信任",
  "数据中心",
];
const FALLBACK_DESCRIPTION = "欧格登智能科技 - 企业级网络安全与IT基础架构综合解决方案服务商";

// 全局 SEO 配置缓存
let _seoCache: { siteTitle: string; siteKeywords: string; siteDescription: string } | null = null;
let _seoPromise: Promise<any> | null = null;

function loadSeoConfig() {
  if (!_seoPromise) {
    _seoPromise = getSeoConfig()
      .then((res: any) => {
        if (res?.data) {
          _seoCache = {
            siteTitle: res.data.siteTitle || "",
            siteKeywords: res.data.siteKeywords || "",
            siteDescription: res.data.siteDescription || "",
          };
        }
      })
      .catch(() => {});
  }
  return _seoPromise;
}

// 启动时立即加载
loadSeoConfig();

/** 获取站点名称（优先使用后台配置） */
export function getSiteName(): string {
  return _seoCache?.siteTitle || FALLBACK_SITE_NAME;
}

/** 获取默认关键词（后台配置 + fallback 合并去重） */
export function getDefaultKeywords(): string[] {
  if (_seoCache?.siteKeywords) {
    const apiKeywords = _seoCache.siteKeywords.split(",").map((k) => k.trim()).filter(Boolean);
    return Array.from(new Set([...apiKeywords, ...FALLBACK_KEYWORDS]));
  }
  return FALLBACK_KEYWORDS;
}

/** 获取默认描述 */
export function getDefaultDescription(): string {
  return _seoCache?.siteDescription || FALLBACK_DESCRIPTION;
}

// 保留向后兼容的导出
export const SITE_NAME = FALLBACK_SITE_NAME;
export const DEFAULT_KEYWORDS = FALLBACK_KEYWORDS;

export interface SEOOptions {
  /** 页面标题（会自动拼接站点名） */
  title: string;
  /** 页面描述（建议 70-160 字） */
  description: string;
  /** 额外关键词，会与默认关键词合并去重 */
  keywords?: string[];
  /** 当前页面路径，例如 "/solutions"（用于 canonical / og:url） */
  path?: string;
  /** 社交分享图绝对 URL */
  image?: string;
  /** 是否禁止搜索引擎收录，默认 false */
  noindex?: boolean;
  /** 扩展结构化数据（JSON-LD） */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  /** og:type，默认 website，文章详情页建议传 article */
  ogType?: "website" | "article" | "product";
}

const JSONLD_TAG_ID = "seo-page-jsonld";

function upsertMeta(
  selector: string,
  attrName: "name" | "property",
  attrValue: string,
  content: string,
) {
  if (!content) return;
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attrName, attrValue);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertCanonical(href: string) {
  let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
}

function removeJsonLd() {
  document.querySelectorAll(`script[data-seo="${JSONLD_TAG_ID}"]`).forEach((n) => n.remove());
}

function injectJsonLd(data: Record<string, unknown> | Record<string, unknown>[]) {
  const list = Array.isArray(data) ? data : [data];
  list.forEach((obj) => {
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.setAttribute("data-seo", JSONLD_TAG_ID);
    s.text = JSON.stringify(obj);
    document.head.appendChild(s);
  });
}

/**
 * 为当前页面设置 SEO 标签：title / description / keywords / canonical
 * / og:* / twitter:* / robots / JSON-LD。
 * 每次 options 变化都会重新写入，页面切换时上一页的 JSON-LD 会被清理。
 */
export function useSEO(options: SEOOptions) {
  const {
    title,
    description,
    keywords = [],
    path,
    image = DEFAULT_IMAGE,
    noindex = false,
    jsonLd,
    ogType = "website",
  } = options;

  const [ready, setReady] = useState(!!_seoCache);

  useEffect(() => {
    if (!_seoCache) {
      loadSeoConfig().then(() => setReady(true));
    }
  }, []);

  useEffect(() => {
    const siteName = getSiteName();
    const fullTitle = title.includes(siteName) ? title : `${title} | ${siteName}`;
    document.title = fullTitle;

    const url = path ? `${SITE_ORIGIN}${path.startsWith("/") ? path : `/${path}`}` : SITE_ORIGIN;
    const defaultKw = getDefaultKeywords();
    const mergedKw = Array.from(new Set([...defaultKw, ...keywords])).join(",");

    upsertMeta('meta[name="description"]', "name", "description", description);
    upsertMeta('meta[name="keywords"]', "name", "keywords", mergedKw);
    upsertMeta(
      'meta[name="robots"]',
      "name",
      "robots",
      noindex
        ? "noindex,nofollow"
        : "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1",
    );

    upsertMeta('meta[property="og:title"]', "property", "og:title", fullTitle);
    upsertMeta('meta[property="og:description"]', "property", "og:description", description);
    upsertMeta('meta[property="og:url"]', "property", "og:url", url);
    upsertMeta('meta[property="og:image"]', "property", "og:image", image);
    upsertMeta('meta[property="og:type"]', "property", "og:type", ogType);
    upsertMeta('meta[property="og:site_name"]', "property", "og:site_name", siteName);
    upsertMeta('meta[property="og:locale"]', "property", "og:locale", "zh_CN");

    upsertMeta('meta[name="twitter:card"]', "name", "twitter:card", "summary_large_image");
    upsertMeta('meta[name="twitter:title"]', "name", "twitter:title", fullTitle);
    upsertMeta('meta[name="twitter:description"]', "name", "twitter:description", description);
    upsertMeta('meta[name="twitter:image"]', "name", "twitter:image", image);

    upsertCanonical(url);

    removeJsonLd();
    if (jsonLd) injectJsonLd(jsonLd);

    return () => {
      // 页面卸载时移除本页 JSON-LD，避免累积
      removeJsonLd();
    };
  }, [title, description, keywords.join("|"), path, image, noindex, ogType, JSON.stringify(jsonLd), ready]);
}

/** 生成面包屑 JSON-LD 结构化数据 */
export function buildBreadcrumbLd(items: { name: string; path: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, idx) => ({
      "@type": "ListItem",
      position: idx + 1,
      name: item.name,
      item: `${SITE_ORIGIN}${item.path.startsWith("/") ? item.path : `/${item.path}`}`,
    })),
  };
}
